import React, { useState, useEffect } from 'react';
import { useLang } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import api from '../utils/api';
import Layout from '../components/Layout';
import { Hash } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function HashtagBrowserPage() {
  const { lang } = useLang();
  const navigate = useNavigate();
  const [hashtags, setHashtags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 640 : false);

  useEffect(() => {
    api.get('/hashtags').then(res => { setHashtags(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const maxCount = Math.max(...hashtags.map(h => h.ticket_count), 1);
  // Cap max size: 1.2rem mobile (conservative), 2rem desktop (cloud effect preserved)
  const minSize = 0.8;
  const maxSize = isMobile ? 1.2 : 2.0;

  return (
    <Layout>
      <div className="animate-fade-in">
        <div className="flex items-center gap-3 mb-6 sm:mb-8">
          <Hash size={22} style={{ color: '#F77F00', flexShrink: 0 }} />
          <h1 data-testid="hashtag-title" className="text-xl sm:text-2xl font-bold text-white truncate">{t('browse_hashtags', lang)}</h1>
          {hashtags.length > 0 && (
            <span className="text-xs sm:text-sm px-2.5 py-0.5 rounded-full whitespace-nowrap flex-shrink-0" style={{ background: 'rgba(247,127,0,0.15)', color: '#F77F00' }}>
              {hashtags.length}
            </span>
          )}
        </div>

        {loading ? (
          <p className="text-center py-12" style={{ color: '#6B7280' }}>{t('loading', lang)}</p>
        ) : hashtags.length === 0 ? (
          <p className="text-center py-16" style={{ color: '#6B7280' }}>{lang === 'fr' ? 'Aucun hashtag trouvé' : 'No hashtags found'}</p>
        ) : (
          <div className="apollo-card p-4 sm:p-8">
            <div className="flex flex-wrap gap-2 sm:gap-3 items-center justify-center">
              {hashtags.map((tag, idx) => {
                const ratio = tag.ticket_count / maxCount;
                const scale = minSize + ratio * (maxSize - minSize);
                return (
                  <button
                    key={tag.name}
                    data-testid={`hashtag-cloud-${tag.name}`}
                    onClick={() => navigate(`/tickets?hashtag=${tag.name}`)}
                    className="inline-flex items-center gap-1 px-3 sm:px-4 py-2 rounded-full font-medium transition-all duration-200 animate-fade-in hover:scale-105"
                    style={{
                      background: 'rgba(247,127,0,0.1)',
                      color: '#F77F00',
                      fontSize: `${scale}rem`,
                      animationDelay: `${idx * 50}ms`,
                      border: '1px solid rgba(247,127,0,0.15)',
                      minHeight: '40px',
                      maxWidth: '100%',
                    }}
                  >
                    <span className="truncate">#{tag.name}</span>
                    <span className="text-xs ml-1 opacity-60 flex-shrink-0">({tag.ticket_count})</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
